const DEFAULT_LINK_STYLE = Object.freeze({
  textColor: "#5B5D73",
  backgroundColor: "transparent",
  borderColor: "transparent",
  hoverTextColor: "#19242F",
  hoverBackgroundColor: "rgba(0,0,0,.04)",
  hoverBorderColor: "transparent",
  activeTextColor: "#19242F",
  activeBackgroundColor: "rgba(0,0,0,.045)",
  activeBorderColor: "transparent",
  activeIndicatorColor: "#F35A3A",
  endIconColor: "#5B5D73",
});

const DEFAULT_MOBILE_LINK_STYLE = Object.freeze({
  textColor: "#1A1A2E",
  backgroundColor: "transparent",
  borderColor: "transparent",
  hoverTextColor: "#1A1A2E",
  hoverBackgroundColor: "#EEF0F5",
  hoverBorderColor: "transparent",
  activeTextColor: "#19242F",
  activeBackgroundColor: "rgba(0,0,0,.045)",
  activeBorderColor: "rgba(26,26,46,.09)",
  activeIndicatorColor: "#F35A3A",
  endIconColor: "#5B5D73",
  focusColor: "#F35A3A",
  fontSize: 16,
  fontWeight: 800,
  activeFontWeight: 900,
  minHeight: 52,
  borderRadius: 18,
});

const DEFAULT_ACTION_STYLE = Object.freeze({
  color: "#19242F",
  backgroundColor: "transparent",
  borderColor: "transparent",
  hoverColor: "#19242F",
  hoverBackgroundColor: "#EEF0F5",
  hoverBorderColor: "#F35A3A",
  badgeBackgroundColor: "#F35A3A",
  badgeTextColor: "#FFFFFF",
  avatarBackgroundColor: "#19242F",
  avatarTextColor: "#FFFFFF",
  mutedTextColor: "#5B5D73",
});

const DEFAULT_COMPACT_ACTION_STYLE = Object.freeze({
  ...DEFAULT_ACTION_STYLE,
  backgroundColor: "#FFFFFF",
  borderColor: "rgba(26,26,46,.09)",
});

const DEFAULT_ACCOUNT_DRAWER_STYLE = Object.freeze({
  containerBackgroundColor: "#FFFFFF",
  containerBorderColor: "rgba(26,26,46,.09)",
  primaryTextColor: "#1A1A2E",
  secondaryTextColor: "#5B5D73",
  avatarBackgroundColor: "#19242F",
  avatarTextColor: "#FFFFFF",
  logoutColor: "#1A1A2E",
  logoutBackgroundColor: "#FFFFFF",
  logoutBorderColor: "rgba(26,26,46,.09)",
  logoutHoverColor: "#1A1A2E",
  logoutHoverBackgroundColor: "#EEF0F5",
  logoutHoverBorderColor: "rgba(26,26,46,.09)",
  logoutFocusColor: "#F35A3A",
});

const DEFAULT_SURFACE_STYLE = Object.freeze({
  backgroundColor: "#FFFFFF",
  borderColor: "rgba(26,26,46,.09)",
  boxShadow: "0 8px 30px rgba(20,25,35,.05)",
});

const DEFAULT_MOBILE_ICON_BUTTON = Object.freeze({
  color: "#19242F",
  backgroundColor: "#FFFFFF",
  borderColor: "rgba(26,26,46,.09)",
  hoverColor: "#19242F",
  hoverBackgroundColor: "#EEF0F5",
  hoverBorderColor: "rgba(26,26,46,.09)",
  focusColor: "#F35A3A",
  size: 42,
});

const DEFAULT_MOBILE_OWNERS = Object.freeze({
  shellStyle: DEFAULT_SURFACE_STYLE,
  barStyle: { ...DEFAULT_SURFACE_STYLE, boxShadow: "none" },
  drawerStyle: { backgroundColor: "#EEF0F5", borderColor: "rgba(26,26,46,.09)", boxShadow: "none" },
  drawerHeaderStyle: { backgroundColor: "#EEF0F5", borderColor: "rgba(26,26,46,.09)", boxShadow: "none" },
  openButtonStyle: DEFAULT_MOBILE_ICON_BUTTON,
  closeButtonStyle: DEFAULT_MOBILE_ICON_BUTTON,
  catalogPanelStyle: { backgroundColor: "#FFFFFF", borderColor: "rgba(26,26,46,.09)", boxShadow: "none" },
  catalogItemStyle: { textColor: "#1A1A2E", mutedTextColor: "#5B5D73", hoverBackgroundColor: "#EEF0F5", focusColor: "#F35A3A" },
});

function objectValue(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function valueOr(value, fallback) {
  const candidate = String(value ?? "").trim();
  return candidate || fallback;
}

function withDefaults(value, defaults) {
  return { ...defaults, ...objectValue(value) };
}

export function navbarSurfaceStyle(navigation, key, device = "desktop") {
  if (device === "mobile" && (key === "shellStyle" || key === "barStyle")) {
    return withDefaults(navigation?.mobile?.[key], DEFAULT_MOBILE_OWNERS[key]);
  }
  return withDefaults(navigation?.[key], key === "barStyle" ? { ...DEFAULT_SURFACE_STYLE, boxShadow: "none" } : DEFAULT_SURFACE_STYLE);
}

export function navbarTopbarStyle(navigation) {
  return withDefaults(navigation?.topbarStyle, {
    backgroundColor: "#19242F",
    borderColor: "rgba(255,255,255,.1)",
    textColor: "#FFFFFF",
    iconColor: "#F35A3A",
    socialIconColor: "#FFFFFF",
    socialHoverBackgroundColor: "rgba(255,255,255,.1)",
  });
}

export function navbarLogoSurfaceStyle(navigation, device = "desktop") {
  return withDefaults(navigation?.logoSurfaceStyle?.[device], {
    backgroundColor: "transparent",
    borderColor: "transparent",
    boxShadow: "none",
    paddingX: 0,
    paddingY: 0,
    borderRadius: 0,
  });
}

export function navbarBusinessNameColor(navigation, device = "desktop") {
  const fallback = device === "drawer" ? "#1A1A2E" : "#19242F";
  return valueOr(navigation?.businessNameStyle?.[device]?.color, fallback);
}

export function navbarLinkStyle(navigation, key, device = "desktop") {
  const owner = objectValue(navigation?.links?.[key]);
  return withDefaults(device === "mobile" ? owner.mobileStyle : owner.desktopStyle, device === "mobile" ? DEFAULT_MOBILE_LINK_STYLE : DEFAULT_LINK_STYLE);
}

export function navbarCustomButtonStyle(button, device = "desktop") {
  const fallback = device === "mobile"
    ? { ...DEFAULT_MOBILE_LINK_STYLE, borderColor: "rgba(26,26,46,.09)", hoverBorderColor: "rgba(26,26,46,.09)" }
    : { ...DEFAULT_LINK_STYLE, borderColor: "rgba(26,26,46,.09)", hoverBorderColor: "#F35A3A" };
  return withDefaults(device === "mobile" ? button?.mobileStyle : button?.desktopStyle, fallback);
}

export function navbarActionStyle(navigation, owner, device = "desktop") {
  const source = owner === "login"
    ? navigation?.loginStyles
    : owner === "account"
      ? navigation?.accountStyles
      : navigation?.cartStyles;
  const fallback = owner === "account" && device === "drawer"
    ? DEFAULT_ACCOUNT_DRAWER_STYLE
    : device === "desktop" && owner === "login"
      ? DEFAULT_ACTION_STYLE
      : DEFAULT_COMPACT_ACTION_STYLE;
  return withDefaults(source?.[device], fallback);
}

export function navbarMobileOwner(navigation, key) {
  return withDefaults(navigation?.mobile?.[key], DEFAULT_MOBILE_OWNERS[key] || {});
}

export function navbarDrawerStyle(navigation) {
  return navbarMobileOwner(navigation, "drawerStyle");
}

export function navbarMobileInfrastructureStyle(navigation) {
  const mobile = objectValue(navigation?.mobile);
  return {
    itemGap: Number.isFinite(Number(mobile.itemGap)) ? Number(mobile.itemGap) : 5.2,
    logoColor: valueOr(mobile.logoColor, ""),
    logoMaxHeight: mobile.logoMaxHeight !== null && mobile.logoMaxHeight !== undefined && mobile.logoMaxHeight !== "" && Number.isFinite(Number(mobile.logoMaxHeight)) ? Number(mobile.logoMaxHeight) : null,
  };
}
